import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import PayPalSubscribeButton from "../../../components/PayPal/PayPalSubscribeButton";
import { getCoralFromAPI, getPayPalPlanIdForPledgeAmount } from '../../../api/api';
import { BaseCoral, ClientCoral } from "shared/types/platformTypes";
import { ArrowLeft } from 'lucide-react';
import HexBloom from '../../../components/HexBloom/HexBloom';
import { DisplayHeading } from '../../../components/editorial';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import { useAuthContext } from '../../../components/Auth/AuthContext/AuthContext';
import ContributionFeeBreakdown from '../../../components/ContributionFeeBreakdown/ContributionFeeBreakdown';
import { pledgePreview } from './paymentFeePreview';

const initialOptions = {
  currency: "AUD",
  vault: true,
  intent: 'subscription'
};

/** Seeds the coral's bloom, so a given coral always carries the same mark. */
const seedFromName = (name: string) => {
  let total = 0;
  for (let i = 0; i < name.length; i++) {
    total += name.charCodeAt(i);
  }
  return total;
};

const Payments: React.FC = () => {
  const { userProfile } = useAuthContext();
  const location = useLocation();
  const { id } = useParams() as { id: string };
  const searchParams = new URLSearchParams(location.search);
  const amount = searchParams.get('amt') ?? undefined;
  const subscriptionId = searchParams.get('sid') ?? undefined;
  const navigate = useNavigate();
  const [planId, setPlanId] = useState<string | null>(null);
  const [coral, setCoral] = useState<BaseCoral | null>(null);
  const [loading, setLoading] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    console.log('checking if id exists');
    if (id) {
      console.log('getting coral from API');

      (async () => {
        let data: ClientCoral;

        if (amount && !isNaN(parseFloat(amount))) {
          data = {
            pledgeAmount: parseFloat(amount),
            guid : id
          } as ClientCoral;
        } else {
          if (!userProfile) {
            throw new Error("If the payment amount is not specified the user must be logged in");
          }
          data = await getCoralFromAPI(id);
        }

        setCoral(data);
      })();
    }
  }, [id, amount, userProfile]);

  useEffect(() => {
    console.log('checking if coral exists');
    if (coral) {
      const fetchPlanId = async () => {

        const data = await getPayPalPlanIdForPledgeAmount(coral.pledgeAmount);
        setPlanId(data.planId);
      };

      console.log('fetching plan id');

      fetchPlanId();
      setLoading(false);

      if (userProfile) {
        trackEvent(EventName.CREATE_CORAL_GOTO_PAYMENT, {
          userId: userProfile!.userId,
          coralId: coral.guid,
          amount: coral.pledgeAmount
        });
      }
    }
  }, [coral, userProfile]);

  const handleBackClick = () => {
    navigate(`/coral/${id}`);
  };

  const notice = (message: string) => (
    <div className="bg-ivory min-h-screen flex justify-center items-center px-4">
      <p className="text-center font-cormorant text-2xl text-editorial-muted">{message}</p>
    </div>
  );

  if (!id) {
    return notice('No Coral ID found');
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-ivory">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-editorial-accent"></div>
      </div>
    );
  }

  if (!coral) {
    return notice('Coral not found');
  }

  // The pledge reaches this page from a query parameter, so it is validated before it reaches
  // the fee producer rather than after.
  const preview = pledgePreview(coral.pledgeAmount);
  if (preview.ok === false) {
    return notice(preview.reason);
  }
  const totalAmount = coral.pledgeAmount;

  return (
    <div className="bg-ivory min-h-screen py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-[0_24px_48px_-24px_rgba(12,19,45,0.18)] border border-editorial-muted/5 overflow-hidden">
        <div className="px-6 pt-6">
          <button
            onClick={handleBackClick}
            className="flex items-center text-editorial-muted/70 hover:text-editorial-muted transition-colors duration-200"
          >
            <ArrowLeft size={18} strokeWidth={1.75} className="mr-2" />
            Back
          </button>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 px-6 pt-4">
          {/* Falls back to the id: arriving with ?amt= synthesises the coral without a name,
              and every coral would otherwise share one mark. */}
          <HexBloom seed={seedFromName(coral.coralName ?? id)} size={132} />
          <DisplayHeading as="h1" scale="heading" measure={false} className="text-center sm:text-left">
            Your Coral Subscription
          </DisplayHeading>
        </div>

        <div className="p-6 space-y-8">
          <div>
            <h2 className="font-cormorant text-2xl text-editorial-ink mb-2">Subscription Details</h2>
            <p className="text-editorial-muted/75 mb-2">
                You&apos;re about to set up a monthly subscription to your coral.
            </p>
            <p className="text-editorial-muted/75">
              {`wearecoral is built on trust based cooperation. You are not purchasing a good or service.
              Your patronage ensures musicians can continue making the music you love.
              You are welcome to stop or modify your subscription at any time.`}
            </p>
          </div>
          <div className="bg-cream p-6 rounded-2xl space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg text-editorial-muted">Total Monthly Pledge</span>
              <span data-testid="pledge-total" className="font-cormorant text-3xl text-editorial-ink tabular-nums">${totalAmount.toFixed(2)}</span>
            </div>
            <ContributionFeeBreakdown
              breakdown={preview.breakdown}
              receiptHeading="Where your pledge goes"
            />
          </div>

          <div>
            <div className="text-sm text-editorial-muted/70 space-y-2 mb-4">
              <h3 className="font-cormorant text-2xl text-editorial-ink">Terms and Conditions:</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your subscription will be processed monthly on the date of your initial payment.</li>
                <li>Funds are held until the end of each month before being distributed to artists.</li>
                <li>You can cancel or modify your subscription at any time through your account settings.</li>
              </ul>
              <p>
                By proceeding, you agree to wearecoral&apos;s{' '}
                <a href="https://www.wearecoral.org/terms" target="_blank" rel="noopener noreferrer" className="text-editorial-accent hover:underline">Terms of Service</a> and{' '}
                <a href="https://www.wearecoral.org/privacy" target="_blank" rel="noopener noreferrer" className="text-editorial-accent hover:underline">Privacy Policy</a>.
              </p>
            </div>
            <h2 className="font-cormorant text-2xl text-editorial-ink mb-2">Payment Method</h2>
            <p className="text-editorial-muted/75 mb-4 text-sm">
              We use PayPal to process your subscription securely. You&apos;ll be redirected to PayPal to complete your payment.
            </p>
            <div className="flex items-center space-x-2 mb-4">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  id="terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-4 h-4 text-editorial-accent bg-cream border-editorial-muted/25 rounded focus:ring-editorial-accent focus:ring-2 cursor-pointer"
                />
                <label
                  htmlFor="terms"
                  className="ml-2 text-sm text-editorial-muted cursor-pointer select-none"
                >
                  I have read, understood, and accepted the 
                  <a 
                    href="https://www.wearecoral.org/terms" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-editorial-accent hover:underline"
                  >
                    {" "}Terms and Conditions
                  </a>.
                </label>
              </div>
            </div>
            {planId && (
              agreedToTerms ? (
                <>
                  {process.env.REACT_APP_PAYPAL_CLIENT_ID ? (
                    <PayPalScriptProvider options={{ ...initialOptions, clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID }}>
                      <PayPalSubscribeButton planId={planId} coralId={id} subscriptionId={subscriptionId} />
                    </PayPalScriptProvider>
                  ) : (
                    <div>Error: REACT_APP_PAYPAL_CLIENT_ID is undefined.</div>
                  )}
                </>
              ) : (
                <button
                  className="w-full py-3 px-4 bg-editorial-muted/10 text-editorial-muted/50 rounded-full cursor-not-allowed"
                  disabled
                >
                  Please agree to the Terms and Conditions
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;